/**
 * Turning "this is wrong" into "here is the edit".
 *
 * A finding that says what to do is worth several that only say what is wrong,
 * and the difference between a suggestion and a fix is whether it comes with
 * coordinates. This module produces the coordinates: real byte ranges in a real
 * file, each carrying the text it expects to find there so `patch.ts` can refuse
 * it if the file has moved on.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS NOT HERE, AND WHY
 *
 * There is no fix for a wrong `handlingId`, a missing model, or a mod kit the
 * variations file names and carcols does not define. Every one of those has
 * several plausible repairs — add the entry, rename the reference, delete the
 * vehicle — and picking one on the author's behalf is how a tool destroys work.
 * Fixes live here only where the intent is unambiguous: a declaration that is
 * missing a line, a path whose capitals do not match the disk, an id that must
 * simply be a different number.
 *
 * Everything here returns `null` rather than guessing when it cannot find the
 * anchor it needs. A fix that lands in the wrong place is far worse than no fix.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { entriesOfKind } from "./manifest";
import { textEdit } from "./patch";
import type { GtaFinding, GtaTextEdit, ManifestEntry, ParsedManifest } from "./types";

/** The manifest a fix is being calculated against, and the text it came from. */
export type FixContext = {
  manifest: ParsedManifest;
  /** The manifest source, exactly as parsed. Offsets are into this. */
  source: string;
};

/**
 * How far past a kit name to look for its id before giving up.
 *
 * Rockstar's own kits put `<id>` on the line after `<kitName>`; a generated one
 * puts it within a few. Searching further would eventually find the *next* kit's
 * id and rewrite the wrong vehicle's mods.
 */
const MAX_KIT_ID_DISTANCE = 400;

// ─── Manifest edits ──────────────────────────────────────────────────────────

/**
 * Add a path to `files{}`.
 *
 * Inserted after the last entry already in the block, so the diff is one line
 * and the author's own ordering survives. When there is no block at all, one is
 * appended whole — a manifest with a `data_file` and no `files{}` is common
 * enough that refusing to fix it would leave the most useful case uncovered.
 */
export function addToFilesBlock(context: FixContext, path: string): GtaTextEdit | null {
  const { manifest, source } = context;
  const existing = entriesOfKind(manifest, "file");

  if (existing.length > 0) {
    const last = existing[existing.length - 1];
    const at = last.span.byteEnd;
    if (typeof at !== "number") return null;
    return textEdit(manifest.file, at, at, "", `,\n  '${path}'`);
  }

  const at = source.length;
  return textEdit(
    manifest.file,
    at,
    at,
    "",
    `${source.endsWith("\n") ? "" : "\n"}\nfiles {\n  '${path}',\n}\n`,
  );
}

/**
 * Add the `data_file` line a meta file needs before the game will read it.
 *
 * Appended rather than threaded in beside its siblings: `data_file` lines are
 * order-independent, and an edit at the end of the file cannot collide with an
 * edit to the `files{}` block above it, which matters because those two fixes
 * are almost always offered together.
 */
export function addDataFileDeclaration(
  context: FixContext,
  type: string,
  path: string,
): GtaTextEdit | null {
  const { manifest, source } = context;
  const at = source.length;
  return textEdit(
    manifest.file,
    at,
    at,
    "",
    `${source.endsWith("\n") ? "" : "\n"}data_file '${type}' '${path}'\n`,
  );
}

/**
 * Replace one declared value in place, keeping the quotes the author used.
 *
 * The span a `ManifestEntry` carries includes its delimiters, so the replacement
 * has to bring its own. Reusing the original opening character means a manifest
 * written in double quotes stays written in double quotes, and a `[[long]]`
 * string is left alone entirely rather than being rewritten into a form the
 * author did not choose.
 */
export function rewriteEntryValue(
  context: FixContext,
  entry: ManifestEntry,
  replacement: string,
): GtaTextEdit | null {
  const { manifest, source } = context;
  const { byteStart, byteEnd } = entry.span;
  if (typeof byteStart !== "number" || typeof byteEnd !== "number") return null;

  const span = source.slice(byteStart, byteEnd);
  const quote = span[0];
  if (quote !== "'" && quote !== '"') return null;
  if (replacement.includes(quote)) return null;

  return textEdit(manifest.file, byteStart, byteEnd, span, `${quote}${replacement}${quote}`);
}

// ─── Carcols edits ───────────────────────────────────────────────────────────

/**
 * Point a mod kit at a different id.
 *
 * Anchored on the kit's own name rather than on a bare `value="0"`, because a
 * carcols file is full of `value=` attributes and rewriting the wrong one moves
 * a siren profile or a light setting instead. If the id is not within a few
 * lines of the name it belongs to, this gives up: at that distance the next
 * kit's id is as likely a match as this one's.
 */
export function retargetModkitId(
  source: string,
  file: string,
  kitName: string,
  newId: number,
): GtaTextEdit | null {
  const nameAt = source.indexOf(`<kitName>${kitName}</kitName>`);
  if (nameAt === -1) return null;

  const window = source.slice(nameAt, nameAt + MAX_KIT_ID_DISTANCE);
  const match = /<id\s+value="(\d+)"\s*\/?>/.exec(window);
  if (!match || match.index === undefined) return null;

  const start = nameAt + match.index;
  const end = start + match[0].length;
  return textEdit(file, start, end, match[0], `<id value="${newId}" />`);
}

// ─── Attaching fixes to findings ─────────────────────────────────────────────

/**
 * Fill in the edits for the findings whose repair is unambiguous.
 *
 * Findings arrive from the analyzers already carrying a `fix.label` — the
 * sentence a button shows — because the analyzer knows what it meant and this
 * module only knows how to spell it. What gets added here is `fix.edits`, and
 * only where the anchor was found: a fix whose edit could not be calculated
 * keeps its label and stays a description, which is honest and still useful.
 */
export function attachManifestFixes(
  findings: readonly GtaFinding[],
  context: FixContext,
): GtaFinding[] {
  const { manifest } = context;

  return findings.map((finding) => {
    if (!finding.fix || finding.fix.edits) return finding;

    const edits = editsFor(finding, context, manifest);
    if (!edits || edits.length === 0) return finding;

    return {
      ...finding,
      tags: finding.tags?.includes("autofixable")
        ? finding.tags
        : [...(finding.tags ?? []), "autofixable"],
      fix: { ...finding.fix, edits },
    };
  });
}

function editsFor(
  finding: GtaFinding,
  context: FixContext,
  manifest: ParsedManifest,
): GtaTextEdit[] | null {
  switch (finding.code) {
    case "manifest.data_file.not_exported": {
      const entry = entriesOfKind(manifest, "data_file").find(
        (candidate) => candidate.span.line === finding.line,
      );
      if (!entry) return null;
      const edit = addToFilesBlock(context, entry.value);
      return edit ? [edit] : null;
    }

    case "manifest.meta.not_registered": {
      // The analyzer's label already names the exact line to add, and it is the
      // only place the file path and its type appear together.
      const match = /data_file '([A-Z_0-9]+)' '(.+)'$/.exec(finding.fix?.label ?? "");
      if (!match) return null;
      const edit = addDataFileDeclaration(context, match[1], match[2]);
      return edit ? [edit] : null;
    }

    case "manifest.path.case_mismatch": {
      const entry = manifest.entries.find((candidate) => candidate.span.line === finding.line);
      const onDisk = /is on disk as (.+)$/.exec(finding.title);
      if (!entry || !onDisk) return null;
      const edit = rewriteEntryValue(context, entry, onDisk[1]);
      return edit ? [edit] : null;
    }

    default:
      return null;
  }
}
